import { useEffect } from 'react';
import { useAolStore } from '../../context/AolContext';
import { useEventStore } from '../../context/EventContext';
import useApiEndpoints from '../../context/useApiEndpoints';
import { useVesselStore } from '../../context/VesselContext';
import { makeAolDataItemList, makeAolGroupList } from '../../factory/Aol/AolDataItemFactory';
import { AolItemFactory } from '../../factory/AolItemFactory';
import { ValidatedNoonReportFactory } from '../../factory/ValidatedNoonReportFactory';
import { VoyageFuelPriceFactory } from '../../factory/VoyageFuelPriceFactory';
import { AolItemBM } from '../../types/BusinessModel/AolItemBM';
import { ValidatedNoonReportBM } from '../../types/BusinessModel/ValidatedNoonReportBM';
import { VoyageFuelPriceBM } from '../../types/BusinessModel/VoyageFuelPriceBM';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import useApi from '../useApi';
import { NoonReportSectionEnum } from '../../types/BusinessModel/AolEnums';
import { addDateDays, getUtcTimeNow, isDateAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import useAppFeature from 'src/app/useAppFeature';
import { formatAolUrlDate, getAolDateFromUrlFormat } from 'src/utils/aol/aolDateUtils';
import { useStowageStore } from 'src/context/StowageContext';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';
import { generateLocalAolItems } from 'src/utils/aol/localAolGenerator';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useRotationStore } from 'src/context/RotationContext';
import { deriveLocalPortCalls } from 'src/utils/aol/deriveLocalPortCalls';
import TankersEventFactory from '../../factory/TankersEventFactory';
import { getAllReportEventsItems } from '../report-events/offlineReportEventsApi';
import { TimeZoneDataBM } from 'src/types/BusinessModel/TimeZoneDataBM';
import { TimeZoneFactory } from 'src/factory/TimeZoneFactory';
import { TimeZoneData } from 'src/types/ViewModel/TimeZoneData';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const aolItemFactory = new AolItemFactory();
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();
    const voyageFuelPriceFactory = new VoyageFuelPriceFactory();
    const { state, getApiData } = useApi();
    const {
        setLastAolFetchTime,
        lastAolFetchTime,
        setAolItems,
        setNoonReports,
        setAolDataItems,
        setAolGroups,
        setVoyageFuelPrices,
        aolEventsArray,
        setAolEventsArray,
        aolGroups,
        aolDataItems,
    } = useAolStore();
    const { lastDeletEventDate, lastEventChangeDate } = useEventStore();
    const { selectedVessel } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { isFeatureEnabled } = useAppFeature();
    const { imosRotationPortCallList, timeZoneData, setTimeZoneData } = useRotationStore();
    const { newEventList } = TankersEventFactory();

    const ensureAolEventsArrayLoaded = async (
        imoNumber: number,
        fromDate: string,
        toDate: string,
        forceRefresh?: boolean
    ): Promise<TankersEvent[] | null> => {
        const isStowageIntegrationActive = isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber });

        if (!isStowageIntegrationActive) return null;

        const hasAolEventsArray = aolEventsArray && aolEventsArray.length > 0;

        if (!hasAolEventsArray || forceRefresh || (!lastAolFetchTime && hasAolEventsArray)) {
            return await loadAolEventsArray(imoNumber, fromDate, toDate);
        }

        return aolEventsArray;
    };

    const loadAolEventsArray = async (imoNumber: number, fromDate: string, toDate: string): Promise<TankersEvent[] | null> => {
        try {
            const fromDateObj = getAolDateFromUrlFormat(fromDate);
            const toDateObj = getAolDateFromUrlFormat(toDate);

            const fromOffsetStr = Math.floor(addDateDays(fromDateObj, -2).getTime() / 1000).toString();
            const toOffsetStr = Math.floor(addDateDays(toDateObj, 2).getTime() / 1000).toString();

            const result = await getApiData({
                endpoint: apiEndpoints['getTankersEventDataForAols'].url,
                queryParam: buildQueryParams({
                    imoNumber,
                    fromOffsetStr,
                    toOffsetStr,
                }),
                forceRefreshCache: true,
            });

            if (result && result.events) {
                const offlineReportEvents = await getAllReportEventsItems();
                const evList = newEventList(result, offlineReportEvents, false);

                if (evList) {
                    const eventArray = [...evList];
                    setAolEventsArray(eventArray);
                    return eventArray;
                }
            }
            return null;
        } catch (error) {
            console.error('Failed to load AOL events array:', error);
            return null;
        }
    };

    const fetchTimeZoneData = async (portIds: number[]): Promise<TimeZoneData | null> => {
        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getTimeZoneData'].url,
                queryParam: buildQueryParams({ portIds }),
            });

            if (result) {
                const timeZoneDataBM = result as TimeZoneDataBM;
                const timeZoneFactory = new TimeZoneFactory();
                const tzData = timeZoneFactory.newTimeZoneData(timeZoneDataBM);
                setTimeZoneData(tzData);
                return tzData;
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch timezone data:', error);
            return null;
        }
    };

    const hasVoyagesInSyncRange = (fromDate: Date, toDate: Date, firstVoyageSync: number | null | undefined): boolean => {
        if (firstVoyageSync === null || firstVoyageSync === undefined) return false;

        const stowagePlanRotation = loadedMasterStowagePlan?.rotation;
        if (!stowagePlanRotation || stowagePlanRotation.length === 0) return false;

        const portCallsInRange = stowagePlanRotation.filter((pc) => {
            const arrivalDate = pc.arrivalDateTimeUtc ? new Date(pc.arrivalDateTimeUtc) : null;
            const departureDate = pc.departureDateTimeUtc ? new Date(pc.departureDateTimeUtc) : null;
            // Port call overlaps with date range if arrival is before toDate and departure is after fromDate
            const arrivalBeforeEnd = arrivalDate ? arrivalDate <= toDate : true;
            const departureAfterStart = departureDate ? departureDate >= fromDate : true;
            return arrivalBeforeEnd && departureAfterStart && pc.voyageNumber;
        });

        if (portCallsInRange.length === 0) return false;

        const minVoyageNumInRange = portCallsInRange.reduce((min, pc) => {
            const voyageNum = parseInt(pc.voyageNumber, 10);
            return !isNaN(voyageNum) && voyageNum < min ? voyageNum : min;
        }, Infinity);

        return minVoyageNumInRange !== Infinity && minVoyageNumInRange >= firstVoyageSync;
    };

    const getAolDataSinceLastUpdate = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        if (!lastAolFetchTime) {
            await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, true);
            getAolData(imoNumber, fromDate, toDate, forceRefreshCache);
            return;
        }

        const hasAolDataLoaded = aolGroups && aolGroups.length > 0 && aolDataItems && aolDataItems.length > 0;

        const hasAolEventsArray = aolEventsArray && aolEventsArray.length > 0;
        let lastEvent: TankersEvent | null = null;
        let hasNewData = false;

        if (hasAolEventsArray) {
            lastEvent = aolEventsArray.sort((a, b) => (isDateSameOrBefore(a.lastUpdated, b.lastUpdated, 'second') ? -1 : 1))[
                aolEventsArray.length - 1
            ];
            hasNewData = lastEvent ? isDateAfter(lastEvent.lastUpdated, lastAolFetchTime, 'second') : false;
        }

        if (!hasNewData && lastEventChangeDate) {
            hasNewData = isDateAfter(lastEventChangeDate, lastAolFetchTime, 'second');
        }

        if (!hasNewData && lastDeletEventDate) {
            hasNewData = isDateAfter(lastDeletEventDate, lastAolFetchTime, 'second');
        }

        if (!hasNewData && hasAolDataLoaded) {
            await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, forceRefreshCache);
            return;
        }

        await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, true);
        getAolData(imoNumber, fromDate, toDate, true);
    };

    const getAolData = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        setAolGroups([]);
        const _fromDate = formatAolUrlDate(addDateDays(getAolDateFromUrlFormat(fromDate), -1));
        const aolItemsPromise = getAolItems(imoNumber, _fromDate, toDate, forceRefreshCache);
        const noonReportsPromise = getNoonReports(imoNumber, _fromDate, toDate, forceRefreshCache);

        const values = await Promise.all([aolItemsPromise, noonReportsPromise]);
        if (!values[0] || !values[1]) return;
        const _aolDataItems = await makeAolDataItemList(values[0], values[1], isFeatureEnabled, selectedVessel, loadedMasterStowagePlan);
        const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
        setAolItems([...values[0]]);
        setNoonReports([...values[1]]);
        setAolGroups([..._aolGroups]);
        setAolDataItems([..._aolDataItems]);
    };

    const getAolItems = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        const isStowageIntegrationActive = isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber });
        const firstVoyageSync = selectedVessel?.firstVoyageOrcaWebStowageImosSync;
        const _fromDate = getAolDateFromUrlFormat(fromDate);
        const _toDate = getAolDateFromUrlFormat(toDate);

        if (isStowageIntegrationActive && hasVoyagesInSyncRange(_fromDate, _toDate, firstVoyageSync)) {
            const currentEventArray = await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, true);

            if (currentEventArray && currentEventArray.length) {
                const stowagePlanPortCalls = loadedMasterStowagePlan?.rotation || null;

                // Ensure timezone data is loaded for DST calculations
                let effectiveTimeZoneData = timeZoneData;
                if (!effectiveTimeZoneData && stowagePlanPortCalls && stowagePlanPortCalls.length > 0) {
                    const portIds = Array.from(new Set(stowagePlanPortCalls.map((pc) => pc.portId)));
                    effectiveTimeZoneData = await fetchTimeZoneData(portIds);
                }

                const portCalls = deriveLocalPortCalls(imosRotationPortCallList, stowagePlanPortCalls, effectiveTimeZoneData);

                // Check if we need to fetch backend AOLs for voyages before firstVoyageSync
                const hasOldVoyages = portCalls.some((pc) => {
                    const voyageNum = pc.voyageNumber ? parseInt(pc.voyageNumber, 10) : null;
                    return voyageNum !== null && voyageNum < firstVoyageSync;
                });

                let backendAolItems: any[] = [];

                // Fetch backend AOLs if there are voyages before the sync cutoff
                if (hasOldVoyages) {
                    const requestKey = `getAolItems:${imoNumber}:${fromDate}:${toDate}`;
                    const backendBms = await requestThrottleManager.execute(requestKey, async () => {
                        const result = await getApiData({
                            endpoint: apiEndpoints['getAolItems'].url,
                            queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                            forceRefreshCache,
                        });
                        return result || [];
                    });

                    // Filter backend AOLs to only include those before firstVoyageSync
                    const filteredBackendBms = backendBms.filter((bm: AolItemBM) => {
                        const voyageNum = bm.voyageNumber ? parseInt(bm.voyageNumber, 10) : null;
                        return voyageNum !== null && voyageNum < firstVoyageSync;
                    });

                    backendAolItems = aolItemFactory.createList(filteredBackendBms);
                }

                // Generate local AOLs
                const localBms = generateLocalAolItems({
                    events: currentEventArray,
                    portCalls,
                    vessel: selectedVessel,
                    fromDate: _fromDate,
                    toDate: _toDate,
                });

                // Filter local AOLs to only include those at or after firstVoyageSync
                const filteredLocalBms = localBms.filter((bm: AolItemBM) => {
                    const voyageNum = bm.voyageNumber ? parseInt(bm.voyageNumber, 10) : null;
                    return voyageNum !== null && voyageNum >= firstVoyageSync;
                });

                const localItems = aolItemFactory.createList(filteredLocalBms);

                // Merge backend and local items, sort by voyage number and date
                const mergedItems = [...backendAolItems, ...localItems].sort((a, b) => {
                    const voyageA = parseInt(a.voyageNumber, 10) || 0;
                    const voyageB = parseInt(b.voyageNumber, 10) || 0;
                    if (voyageA !== voyageB) return voyageA - voyageB;

                    // If same voyage, sort by start time
                    const dateA = new Date(a.portLogAolItem?.startTimeLocalTime || 0).getTime();
                    const dateB = new Date(b.portLogAolItem?.startTimeLocalTime || 0).getTime();
                    return dateA - dateB;
                });

                setLastAolFetchTime(getUtcTimeNow());
                return mergedItems;
            }
        }

        // Fallback to backend API if stowage integration is not active, no voyages in sync range, or no events
        const requestKey = `getAolItems:${imoNumber}:${fromDate}:${toDate}`;
        return requestThrottleManager.execute(requestKey, async () => {
            const result = await getApiData({
                endpoint: apiEndpoints['getAolItems'].url,
                queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                forceRefreshCache,
            });
            if (result) return aolItemFactory.createList(result);
            return [];
        });
    };

    const getNoonReports = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        const requestKey = `getNoonReportsForShip:${imoNumber}:${fromDate}:${toDate}`;

        return requestThrottleManager.execute(requestKey, async () => {
            const result = await getApiData({
                endpoint: apiEndpoints['getNoonReportsForShip'].url,
                queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                forceRefreshCache,
            });

            if (result) {
                return validatedNoonReportFactory.createList(result);
            }
            return null;
        });
    };

    const getNoonReportSection = async (imoNumber: number, noonReportId: number, section: NoonReportSectionEnum, forceRefreshCache?: boolean) => {
        const result = await getApiData({
            endpoint: apiEndpoints['getNoonReportSection'].url,
            queryParam: buildQueryParams({ imoNumber, noonReportId, section }),
            forceRefreshCache,
        });

        if (result) return result as ValidatedNoonReportBM;
    };

    const getVoyageFuelPrices = (imoNumber: number) =>
        getApiData({
            endpoint: apiEndpoints['getVoyageFuelPrices'].url,
            queryParam: buildQueryParams({ imoNumber }),
        });

    const getDistanceBetween = async (imoNumber: number, fromDate: string, toDate: string) => {
        const result = await getApiData({
            endpoint: apiEndpoints['getDistanceBetween'].url,
            queryParam: buildQueryParams({ imoNumber, from: fromDate, to: toDate }),
            additionalInfo: { hideLoader: true },
            callInBackground: true,
        });

        if (result) {
            const distanceBetween = result as number;
            return distanceBetween;
        }
    };

    useEffect(() => {
        if (state.status === 'success') {
            if (state.endpoint === apiEndpoints['getAolItems'].url) {
                const bms = state.result as AolItemBM[];
                const aolItems = aolItemFactory.createList(bms);
                setAolItems([...aolItems]);
                setLastAolFetchTime(getUtcTimeNow());
            }
            if (state.endpoint === apiEndpoints['getNoonReportsForShip'].url) {
                const bms = state.result as ValidatedNoonReportBM[];
                const noonReports = validatedNoonReportFactory.createList(bms);
                setNoonReports([...noonReports]);
            }

            if (state.endpoint === apiEndpoints['getVoyageFuelPrices'].url) {
                const bms = state.result as VoyageFuelPriceBM[];
                const voyageFuelPrices = voyageFuelPriceFactory.createList(bms);
                setVoyageFuelPrices([...voyageFuelPrices]);
            }
        } else {
            processApiEndpointError(state);
        }
    }, [state]);

    return {
        state,
        getAolItems,
        getNoonReports,
        getNoonReportSection,
        getAolDataSinceLastUpdate,
        getAolData,
        getVoyageFuelPrices,
        getDistanceBetween,
        ensureAolEventsArrayLoaded,
    };
};
